import Button from "@/components/bricks/button/Button";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import * as Yup from "yup";
import EmailIcon from "../icons/EmailIcon";
import Modal, { ModalContext } from "../Modal/Modal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../select/Select";
import EmailEditor from "./EmailEditor";
import { useEditor, Editor } from "@tiptap/react";
import Recipients from "./Recipients";
import { emailEditorConfig } from "../../config/editorConfigs";
import { Formik, Form, Field, FormikHelpers } from "formik";
import Skabeloner from "./Skabeloner";
import { Dialog } from "radix-ui";
import { sendEmails } from "../../util/email";
import { WizardReceipt } from "../wizards/common/wizardReceipt";
import Vedhaeftelse from "./Vedhaeftelse";
import AttachmentIcon from "../icons/AttachmentIcon";
import { acceptedFileTypes } from "../../config/editorConfigs";
import { SHARED_EMAILS } from "../../constants/common";
import InputErrorMessage from "../InputErrorMessage";
import { UserQueryContext } from "../../context/UserContext";
import ValidationErrorMessage from "../formcontrols/ValidationErrorMessage";

// NOTE: these props should be cleaned up ...
export default function SendEmailModal({
  type,
  contextData: { selectedIds, totalCount, allSelected = false, typeId, email },
  clear,
  setClear,
}: {
  type: IEmailTypes;
  contextData: {
    selectedIds: string[];
    email?: string;
    totalCount: number;
    allSelected?: boolean;
    typeId?: string;
  };
  clear: boolean;
  setClear: Dispatch<SetStateAction<boolean>>;
}) {
  const { user } = useContext(UserQueryContext);
  const editor = useEditor(emailEditorConfig) as Editor;
  const [wizard, setWizard] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setModalOpen } = useContext(ModalContext);
  useEffect(() => {
    if (clear && editor) {
      editor.commands.clearContent();
      setClear(false);
    }
  }, [editor, clear, setClear]);
  if ((type === "kursus" || type === "fmulinje") && !typeId) {
    throw new Error('typeId is required when type is "Kursus" or "Linje"');
  }
  if (!editor) return null;
  const numberSelected = allSelected ? totalCount : selectedIds.length;

  function findNameByEmail(emailToSearch: string): string {
    const matchingPerson = SHARED_EMAILS.find(
      (person) => person.email === emailToSearch,
    );
    return matchingPerson ? matchingPerson.navn : user.name;
  }

  const addMailsToQueue = async (
    values: ISendEmailControl,
    actions: FormikHelpers<ISendEmailControl>,
  ) => {
    setWizard(true);
    setLoading(true);
    const {
      afsenderEmailAdresse,
      additionalRecipients,
      vedhaeftelser,
      emne,
      signatur,
    } = values;
    const afsenderNavn = findNameByEmail(afsenderEmailAdresse);
    const result = await sendEmails(
      {
        type,
        typeId,
      },
      {
        ids: selectedIds,
        afsenderEmailAdresse,
        afsenderFuldtNavn: afsenderNavn,
        emne,
        indhold: editor.getHTML(),
        vedhaeftelser,
        additionalRecipients,
        signatur,
      },
      actions,
    );
    setLoading(false);
    if (!result) {
      setWizard(false);
    } else {
      await new Promise((r) => setTimeout(r, 2000));
      setModalOpen(false);
      setWizard(false);
    }
  };

  return (
    <Modal
      className="c-modal--email"
      title={
        <>
          <EmailIcon height={18} /> Send e-mail
        </>
      }
    >
      {!wizard ? (
        <Formik
          enableReinitialize
          initialValues={
            {
              afsenderEmailAdresse: "",
              signatur: "true",
              emne: "",
              vedhaeftelser: [],
              additionalRecipients: "kandidat",
            } as ISendEmailControl
          }
          validationSchema={Yup.object({
            afsenderEmailAdresse: Yup.string()
              .email("E-mail har forkert format")
              .required("Der skal angives en emailadresse for afsender"),
            signatur: Yup.string().required("Signatur skal være valgt"),
            emne: Yup.string().required(
              "Der kræves et emne for at sende en mail",
            ),
          })}
          onSubmit={addMailsToQueue}
        >
          {(formik) => {
            const {
              handleChange,
              handleSubmit,
              setFieldValue,
              values,
              errors,
              status,
              touched,
              validateForm,
            } = formik;
            return (
              <Form
                className="d-flex flex-column h-100"
                onSubmit={handleSubmit}
              >
                <div className="l-email">
                  <div className="bg-light px-2 py-2">
                    <div className="w-100 pe-2 py-1 d-flex justify-content-between">
                      <Skabeloner
                        editor={editor}
                        type={type}
                        setFieldValue={async function (
                          field: string,
                          value: any,
                          shouldValidate?: boolean | undefined,
                        ) {
                          await setFieldValue(field, value, shouldValidate);
                          validateForm();
                        }}
                      />
                      <label
                        aria-label="Vedhæft filer"
                        className="icon-bg btn--icon border-radius btn-secondary rounded"
                        style={{ cursor: "pointer" }}
                        color="secondary"
                      >
                        <AttachmentIcon />
                        <input
                          name="files"
                          accept={acceptedFileTypes}
                          style={{ display: "none" }}
                          type="file"
                          id="input"
                          multiple
                          onChange={async (e) => {
                            const targetFiles = e.target.files;
                            if (targetFiles) {
                              const newFiles = Array.from(targetFiles);
                              const existingFileNames = values.vedhaeftelser
                                ? values.vedhaeftelser.map((f) =>
                                    f instanceof File ? f.name : f.navn,
                                  )
                                : [];
                              const uniqueNewFiles: IVedhaeftelse[] = newFiles
                                .filter(
                                  (nf) =>
                                    existingFileNames.indexOf(nf.name) < 0,
                                )
                                .map((f) => {
                                  return {
                                    navn: f.name,
                                    mediaType: f.type,
                                    dokument: f,
                                  };
                                });
                              const files = values.vedhaeftelser
                                ? [...values.vedhaeftelser, ...uniqueNewFiles]
                                : uniqueNewFiles;
                              setFieldValue("vedhaeftelser", files);
                            }
                          }}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="l-email-inputs">
                    <div className="l-email-input">
                      <h5 className="form-label">Til:</h5>
                      <div className="d-flex justify-content-between w-100 align-items-center">
                        <Recipients
                          type={type}
                          email={email ? email : undefined}
                          count={numberSelected}
                          handleChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="l-email-input">
                      <label className="form-label">Fra:</label>
                      <Field
                        component={Select}
                        as="select"
                        name="afsenderEmailAdresse"
                        onValueChange={(value: string) => {
                          handleChange({
                            target: { name: "afsenderEmailAdresse", value },
                          });
                        }}
                        value={values.afsenderEmailAdresse ?? undefined}
                      >
                        <SelectTrigger
                          aria-label="Afsender"
                          placeholder="Vælg afsender..."
                          className={`c-form-badge form-select--email${
                            touched.afsenderEmailAdresse &&
                            errors.afsenderEmailAdresse
                              ? " is-invalid"
                              : ""
                          }`}
                        />
                        <SelectContent>
                          {user.email && (
                            <SelectItem value={user.email} key={user.email}>
                              {user.name + " "}
                              <span className="text-muted">{user.email}</span>
                            </SelectItem>
                          )}
                          {SHARED_EMAILS.map((email) => (
                            <SelectItem value={email.email} key={email.email}>
                              {email.navn + " "}
                              <span className="text-muted">{email.email}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>{" "}
                        <ValidationErrorMessage
                          touched={touched.afsenderEmailAdresse}
                          errorMessage={errors.afsenderEmailAdresse}
                        />
                      </Field>
                      <label className="form-label ms-auto">Signatur:</label>

                      <Field
                        component={Select}
                        as="select"
                        name="signatur"
                        onValueChange={(value: string) => {
                          handleChange({
                            target: { name: "signatur", value },
                          });
                        }}
                        defaultValue="true"
                      >
                        <SelectTrigger
                          aria-label="Signatur"
                          placeholder="Vælg signatur"
                          className="c-form-badge form-select--email"
                        />
                        <SelectContent>
                          <SelectItem value="true">Automatisk</SelectItem>
                          <SelectItem value="false">Ingen signatur</SelectItem>
                        </SelectContent>
                      </Field>
                    </div>
                    <div className="l-email-input">
                      <label className="form-label" htmlFor="email-emne">
                        Emne:
                      </label>
                      <Field
                        type="text"
                        id="email-emne"
                        name="emne"
                        onChange={handleChange}
                      />
                      {errors.emne && touched.emne && (
                        <InputErrorMessage
                          className="text-center mb-2 text-nowrap"
                          value={errors.emne}
                          id="feedback"
                        />
                      )}
                    </div>
                  </div>
                  <div className="position-relative d-flex flex-column h-100 overflow-hidden">
                    <EmailEditor emailType={type} editor={editor} />
                    {values.vedhaeftelser.length > 0 && (
                      <div className="l-email-attachments">
                        {values.vedhaeftelser.map((f, i) => {
                          return (
                            <Vedhaeftelse
                              key={i}
                              fil={f}
                              onRemove={() => {
                                const newFiles = values.vedhaeftelser
                                  ? values.vedhaeftelser.filter((fil) => {
                                      if (
                                        "dokument" in fil &&
                                        "dokument" in f
                                      ) {
                                        return fil.navn !== f.navn;
                                      } else if (
                                        "dokument" in fil ||
                                        "dokument" in f
                                      ) {
                                        return true;
                                      } else {
                                        return fil.name !== f.name;
                                      }
                                    })
                                  : [];
                                setFieldValue("vedhaeftelser", newFiles);
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {status && (
                    <InputErrorMessage
                      className="px-4 pb-4"
                      id="feedback"
                      value={status}
                    />
                  )}
                </div>

                {status && <div>{status}</div>}
                <div className="c-modal-footer justify-content-between px-4">
                  <Dialog.Close asChild>
                    <Button color="secondary" type="button" onClick={() => {}}>
                      Annuller
                    </Button>
                  </Dialog.Close>
                  <Button
                    color="primary"
                    type="submit"
                    disabled={
                      loading || errors.emne || editor.getHTML() === "<p></p>"
                    }
                  >
                    Send
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      ) : (
        <>
          <WizardReceipt
            className="mt-5"
            icon={loading ? "loading" : "success"}
            heading={
              loading
                ? `Sender mail${numberSelected === 1 ? "" : "s"}`
                : `Mail${numberSelected === 1 ? "" : "s"} sendt!`
            }
          />
          {/*allSelected && <SendEmailToPersons className="mb-5" />*/}
        </>
      )}
    </Modal>
  );
}
